/**
 * SSE Stream Reading Utilities
 *
 * Shared utilities for processing Server-Sent Events streams.
 * Used by useStreamingChat.
 */

import { parseSSEEvent } from './sse';
import type { StreamingEvent } from '../api/types';
import { parseRetryAfter } from './httpUtils';
import {
  COLD_START_503_DEFAULT_DELAY_MS,
  COLD_START_503_MAX_DELAY_MS,
  STREAM_CONNECT_NETWORK_RETRIES,
  STREAM_CONNECT_NETWORK_RETRY_BASE_DELAY_MS,
  STREAM_CONNECT_NETWORK_RETRY_MAX_DELAY_MS,
  STREAM_CONNECT_NETWORK_RETRY_JITTER_RATIO,
} from './constants';
import { createLogger } from '@/utils/logger';

const logger = createLogger('StreamReader');

/**
 * Options for initiating an SSE streaming request.
 */
export interface InitiateStreamOptions {
  /** Full URL to POST to */
  url: string;
  /** Form data body */
  formData: FormData;
  /** Auth token (null if unauthenticated) */
  token: string | null;
  /** Abort signal for cancellation */
  signal: AbortSignal;
  /**
   * Stable Idempotency-Key for this logical send. Sent as the `Idempotency-Key`
   * header so a resend — whether the abort-aware network retry below or a
   * user-tapped retry — maps to the SAME turn server-side instead of creating a
   * duplicate (see api-service-go handleAsyncTurn). MUST stay stable across all
   * retries of the same message; mint a fresh one only for a new message.
   */
  idempotencyKey: string;
  /** Optional extra headers (e.g., X-Request-ID) */
  extraHeaders?: Record<string, string>;
}

/**
 * Wait `ms`, resolving early if `signal` aborts mid-wait. Shared by the
 * cold-start (503) delay and the network-error backoff so neither hangs the
 * full delay when the user cancels — the subsequent resend then rejects with
 * the canonical fetch AbortError the caller already suppresses.
 */
export function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      resolve();
    };
    // Remove the listener when the timer wins so a longer-lived signal (the
    // per-request controller now outlives a single delay) doesn't retain the
    // closure until GC. The abort path self-removes via `{ once: true }`.
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Result of initiating a chat request. The endpoint always returns 202 with
 * {turn_id, assistant_message_id} (kind 'accepted') — compute runs on a
 * durable worker; the answer arrives as turn.* events on GET /events.
 */
export type StreamResult = { kind: 'accepted'; turnId: string; assistantMessageId: string };

/**
 * Initiate a chat POST request.
 *
 * Handles: FormData POST, auth header, error response parsing. Returns a
 * `StreamResult` for the accepted async turn (202). Throws Error on HTTP error.
 */
export async function initiateStream(
  options: InitiateStreamOptions,
): Promise<StreamResult> {
  const { url, formData, token, signal, idempotencyKey, extraHeaders } = options;

  const send = (): Promise<Response> =>
    fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'omit',
      headers: {
        Accept: 'text/event-stream',
        'Idempotency-Key': idempotencyKey,
        ...(token && { Authorization: `Bearer ${token}` }),
        ...extraHeaders,
      },
      signal,
    });

  // Resend on a transient CONNECTION failure (fetch throws TypeError: DNS
  // hiccup, dropped socket, wifi/cell handoff). This is the low-network case —
  // the request never landed (or its response was lost), so the calm response
  // is a bounded backoff-and-resend, not an immediate error bubble. Without
  // this, a single blip while uploading a medication photo surfaced an error
  // the user had to retry by hand ("gave up too quickly"). Safe to resend: the
  // Idempotency-Key maps a duplicate POST to the same turn server-side.
  //
  // Only TypeError is retried. An AbortError (user cancel / unmount) propagates
  // untouched so cancel stays instant, and an HTTP error response (4xx/5xx) is
  // NOT a thrown error — it returns below and is handled by the 503 / !ok paths.
  const sendWithNetworkRetry = async (): Promise<Response> => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await send();
      } catch (err) {
        const isNetworkError = err instanceof TypeError;
        if (!isNetworkError || attempt >= STREAM_CONNECT_NETWORK_RETRIES) {
          throw err;
        }
        // Capped exponential backoff + jitter (distributed-systems rules): cap
        // the exponential term first, then add up to +20% random skew so a ward
        // of clients that dropped together don't retry in lockstep.
        const cappedMs = Math.min(
          STREAM_CONNECT_NETWORK_RETRY_BASE_DELAY_MS * 2 ** attempt,
          STREAM_CONNECT_NETWORK_RETRY_MAX_DELAY_MS,
        );
        const backoffMs =
          cappedMs + Math.random() * cappedMs * STREAM_CONNECT_NETWORK_RETRY_JITTER_RATIO;
        logger.warn('network error on stream connect; retrying after backoff', {
          attempt: attempt + 1,
          maxRetries: STREAM_CONNECT_NETWORK_RETRIES,
          backoffMs,
        });
        await abortableDelay(backoffMs, signal);
        // A cancel during the backoff means the next send() rejects with
        // AbortError (a TypeError check above won't match it), so it surfaces
        // immediately rather than burning the remaining retries.
      }
    }
  };

  let response = await sendWithNetworkRetry();

  // Cold-start grace: a 503 from the load balancer means the instance is still
  // starting (scale-to-zero is the design, not an outage). The 503 is rejected
  // at the LB before the handler runs, so the turn was never created — it is
  // safe to wait per Retry-After and resend ONCE before surfacing anything.
  // Mirrors the JSON client's 503 contract so the first message to a warming
  // backend keeps the calm typing indicator instead of flashing a "Network
  // error" bubble. Only a second 503 (or any other failure) surfaces.
  // If the user aborts during the wait, the resend rejects with the same
  // AbortError the caller already suppresses.
  if (response.status === 503) {
    const retryAfterHeader = response.headers.get('Retry-After');
    const retryAfter = parseRetryAfter(retryAfterHeader);
    // Cap a server-sent Retry-After so a misconfigured/hostile `Retry-After:
    // 3600` can't freeze the bedside UI for an hour (the nurse could cancel,
    // but the visible effect is an opaque hang). Mirrors the JSON client.
    const delayMs =
      retryAfter !== null
        ? Math.min(retryAfter * 1000, COLD_START_503_MAX_DELAY_MS)
        : COLD_START_503_DEFAULT_DELAY_MS;
    // Mirror the JSON client's cold-start log so a stream retry isn't invisible
    // in production (an outage vs. a silent retry storm must be distinguishable).
    logger.warn('503 on stream connect; retrying after cold-start delay', {
      delayMs,
      retryAfter: retryAfterHeader,
    });
    // Abort-aware wait: if the user cancels mid-delay, stop waiting immediately
    // instead of hanging up to COLD_START_503_MAX_DELAY_MS — the resend below
    // then rejects with the canonical fetch AbortError the caller suppresses.
    // The resend is also network-retry-wrapped so a blip during cold-start
    // recovery doesn't undo the grace.
    await abortableDelay(delayMs, signal);
    response = await sendWithNetworkRetry();
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const errorObj = typeof errorData === 'object' && errorData !== null
      ? (errorData as Record<string, Record<string, unknown> | undefined>)
      : {};
    const errorMessage =
      (typeof errorObj.detail?.message === 'string' ? errorObj.detail.message : undefined) ??
      (typeof errorObj.error?.message === 'string' ? errorObj.error.message : undefined) ??
      'Failed to start streaming';
    throw new Error(errorMessage);
  }

  // 202 Accepted = async chat turn. The body is a small JSON envelope; the
  // answer is delivered via turn.* events on GET /events.
  if (response.status === 202) {
    const data: unknown = await response.json().catch(() => ({}));
    const o =
      typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
    if (typeof o.turn_id !== 'string' || typeof o.assistant_message_id !== 'string') {
      throw new Error('Malformed 202 async-turn response');
    }
    return { kind: 'accepted', turnId: o.turn_id, assistantMessageId: o.assistant_message_id };
  }

  // Any non-202 success is unexpected — the endpoint always returns 202.
  throw new Error(`Unexpected response status ${response.status}; expected 202`);
}

/**
 * Process an SSE stream from a ReadableStream body
 *
 * @param body - The ReadableStream body from fetch response
 * @param onEvent - Callback for each parsed event
 */
export async function processSSEStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: StreamingEvent) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEventType = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEventType = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          const data = line.slice(6);
          const event = parseSSEEvent(currentEventType, data);

          if (event) {
            onEvent(event);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Generate a temporary message ID for optimistic updates
 */
export function generateTempMessageId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
